/**
 * Branch visibility and access helpers for dealer branch deactivation (hidden).
 *
 * Central place for usable-branch filtering, hide permissions, and forced
 * default-branch switch notification policy. Callers should not re-implement
 * these rules in UI or routing.
 *
 * Rules:
 * - Dealer users may only use non-hidden branches.
 * - Cyncly Staff and supplier agents may use hidden branches (support).
 * - Hide/unhide: Cyncly Staff, Staff Admin, or supplier support
 * - Name lookup uses the full branch list (store / admin API), not these filters.
 * - `shouldNotifyForcedBranchSwitch` decides when an automatic default-branch
 *   change should be announced; UI holds any one-time display flag.
 */
import { Branch } from '../../api/dealer-api-interface-franchisee';
import { emptyGuid } from '../../../webmodule-common/other/api/guid';
import { validId } from '../../../webmodule-common/other/ui/string-helper-functions';

/**
 * True when this user may open / switch onto a hidden branch.
 * `canAccessHidden` is true for Cyncly Staff and supplier agents (not dealer users).
 */
export function canUseHiddenBranches(canAccessHidden: boolean): boolean {
  return canAccessHidden;
}

/**
 * True when this user may toggle the Hidden flag on a branch.
 * Allowed: Cyncly Staff, Staff Admin, or supplier support session.
 * Branch Admin and other dealer users are not allowed.
 */
export function canChangeBranchHidden(isStaff: boolean, isAdmin: boolean, isSupplierOrAgent = false): boolean {
  if (isStaff || isSupplierOrAgent) return true;
  return isAdmin;
}

/** Branch is usable for the current user role. */
export function isBranchUsable(branch: Branch, canAccessHidden: boolean): boolean {
  return canUseHiddenBranches(canAccessHidden) || !branch.hidden;
}

/** Branches the user may pick in switchers, dashboards, staff assignment, etc. */
export function filterUsableBranches(branches: Branch[] | null | undefined, canAccessHidden: boolean): Branch[] {
  if (!branches?.length) return [];
  if (canUseHiddenBranches(canAccessHidden)) return branches;
  return branches.filter(b => !b.hidden);
}

export function hasUsableBranches(branches: Branch[] | null | undefined, canAccessHidden: boolean): boolean {
  return filterUsableBranches(branches, canAccessHidden).length > 0;
}

/**
 * Look up a single preferred branch when it is usable for this user.
 * Does not fall back — callers chain multiple candidates themselves
 * (e.g. last-selected ?? server-default ?? first usable).
 */
export function findUsableBranch(
  branches: Branch[] | null | undefined,
  preferredId: string | null | undefined,
  canAccessHidden: boolean
): Branch | null {
  if (!branches?.length || !validId(preferredId)) return null;
  return branches.find(b => b.id === preferredId && isBranchUsable(b, canAccessHidden)) ?? null;
}

/**
 * Pick a sensible working branch when no preferred id is usable.
 * Preference order: first non-hidden → any branch (Cyncly Staff / supplier
 * agents only when all are hidden) → null.
 */
export function pickUsableBranch(branches: Branch[] | null | undefined, canAccessHidden: boolean): Branch | null {
  if (!branches?.length) return null;

  // Prefer a non-hidden branch even for staff/supplier agents when one is available.
  const visible = branches.find(b => !b.hidden);
  if (visible) return visible;

  return canUseHiddenBranches(canAccessHidden) ? (branches[0] ?? null) : null;
}

/**
 * Choose the session default branch by chaining candidates in preference order:
 * last-selected (multi-branch users only) → server default → first usable.
 *
 * Shared by CurrentUserDataStore so default-branch resolution has one implementation.
 */
export function chooseDefaultBranch(args: {
  branches: Branch[] | null | undefined;
  canAccessHidden: boolean;
  allBranchAccess: boolean;
  localPreferredBranchId: string | null | undefined;
  serverDefaultBranchId: string | null | undefined;
}): Branch | null {
  const preferredId = args.allBranchAccess ? (args.localPreferredBranchId ?? emptyGuid) : emptyGuid;
  return (
    findUsableBranch(args.branches, preferredId, args.canAccessHidden) ??
    findUsableBranch(args.branches, args.serverDefaultBranchId, args.canAccessHidden) ??
    pickUsableBranch(args.branches, args.canAccessHidden)
  );
}

/**
 * Whether an automatic default-branch reassignment should notify the user.
 *
 * True when the user moved from one real default branch to another and still
 * has at least one usable branch. False on first login, empty defaults, or
 * when no usable branches remain (handled by the no-branches page instead).
 */
export function shouldNotifyForcedBranchSwitch(
  previousDefaultBranchId: string | null | undefined,
  nextDefaultBranchId: string | null | undefined,
  branches: Branch[] | null | undefined,
  canAccessHidden: boolean
): boolean {
  if (!isRealBranchId(previousDefaultBranchId) || !isRealBranchId(nextDefaultBranchId)) return false;
  if (previousDefaultBranchId === nextDefaultBranchId) return false;
  return hasUsableBranches(branches, canAccessHidden);
}

function isRealBranchId(id: string | null | undefined): id is string {
  return validId(id);
}

/**
 * Count active users assigned only to `branchId` (single-branch assignees).
 * Inactive logins are ignored. Multi-branch (AllBranches) users appear under
 * every branch key and are excluded.
 */
export function countSingleBranchUsers(
  branchUsers: { [key: string]: { userId: string; isActive?: boolean }[] } | undefined,
  branchId: string
): number {
  if (!branchUsers) return 0;

  // Missing isActive is treated as active.
  const isActiveUser = (u: { isActive?: boolean }) => u.isActive !== false;

  const onThisBranch = (branchUsers[branchId] ?? []).filter(isActiveUser);
  if (onThisBranch.length === 0) return 0;

  const multiBranchUserIds = new Set<string>();
  for (const [id, users] of Object.entries(branchUsers)) {
    if (id === branchId) continue;
    for (const u of users) {
      if (isActiveUser(u)) multiBranchUserIds.add(u.userId);
    }
  }

  const singleBranchIds = new Set(onThisBranch.filter(u => !multiBranchUserIds.has(u.userId)).map(u => u.userId));
  return singleBranchIds.size;
}

/**
 * Client-side checks before deactivating (hiding) a branch.
 * Returns issue kinds for the caller to localize; empty means valid to save.
 */
export type HideBranchValidationIssue =
  | { kind: 'permission' }
  | { kind: 'last-active' }
  | { kind: 'single-branch-users'; count: number };

export function validateHideBranch(args: {
  isCreating: boolean;
  currentHidden: boolean;
  originalHidden: boolean;
  canChangeHidden: boolean;
  allBranches?: Branch[];
  currentBranchId: string;
  branchUsers?: { [key: string]: { userId: string; isActive?: boolean }[] };
}): HideBranchValidationIssue[] {
  const issues: HideBranchValidationIssue[] = [];
  if (args.isCreating) return issues;

  const hiddenChanged = args.currentHidden !== args.originalHidden;
  if (hiddenChanged && !args.canChangeHidden) {
    issues.push({ kind: 'permission' });
  }

  // Last-active and single-branch-user rules apply only when hiding (active → hidden).
  if (!(hiddenChanged && args.currentHidden)) return issues;

  // Fail closed when the caller omits the branch list.
  // Without allBranches we cannot prove another active branch remains.
  if (!args.allBranches?.length) {
    issues.push({ kind: 'last-active' });
  } else {
    const otherActive = args.allBranches.filter(b => !b.hidden && b.id !== args.currentBranchId);
    if (otherActive.length === 0) issues.push({ kind: 'last-active' });
  }

  const count = countSingleBranchUsers(args.branchUsers, args.currentBranchId);
  if (count > 0) issues.push({ kind: 'single-branch-users', count });

  return issues;
}
